/**
 * @param {String} str The string value to be hashed
 * @returns Properly formatted SHA256 hash in string format
 */
const toSha256 = async (str: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hash = await crypto.subtle.digest('SHA-256', data);

  return Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Normalizes a string to lowercase, removes non-alphabetic characters
 * @param {string} str
 * @returns Normalized string
 */
const normalizeString = (str: string): string => {
  return str.toLowerCase().replace(/[^a-z]/g, '');
};

/**
 * Normalize email addresses to be lowercase and trimmed
 * @param {string} email
 * @returns Normalized email address string
 */
const normalizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

/**
 * Normalizes a phone number to include country code and only contain numbers
 * @param {string} phoneNumber Raw phone number string
 * @returns Normalized phone number string with country code
 */
// TODO: implement international phone number normalization
const normalizePhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters
  let normalized = phoneNumber.replace(/\D/g, '');

  // Remove leading zeros
  normalized = normalized.replace(/^0+/, '');

  // If the number doesn't start with a country code (assuming US as default),
  // prepend '1' as the country code
  if (!/^[1-9]/.test(normalized)) {
    normalized = '1' + normalized;
  }

  return normalized;
};
/**
 * Normalizes a zip code to lowercase, no spaces, no dash, and first 5 digits for US
 * @param {string} zipCode
 * @returns Normalized zip code string
 */
const normalizeZipCode = (zipCode: string): string => {
  return zipCode.toLowerCase().replace(/[\s-]/g, '').slice(0, 5);
};

/**
 * Normalizes a state to 2-character lowercase code or lowercase with no punctuation, special characters, or spaces
 * @param {string} state
 * @returns Normalized state string
 */
const normalizeState = (state: string): string => {
  const stateAbbreviations: { [key: string]: string } = {
    alabama: 'al',
    alaska: 'ak',
    arizona: 'az',
    arkansas: 'ar',
    california: 'ca',
    colorado: 'co',
    connecticut: 'ct',
    delaware: 'de',
    florida: 'fl',
    georgia: 'ga',
    hawaii: 'hi',
    idaho: 'id',
    illinois: 'il',
    indiana: 'in',
    iowa: 'ia',
    kansas: 'ks',
    kentucky: 'ky',
    louisiana: 'la',
    maine: 'me',
    maryland: 'md',
    massachusetts: 'ma',
    michigan: 'mi',
    minnesota: 'mn',
    mississippi: 'ms',
    missouri: 'mo',
    montana: 'mt',
    nebraska: 'ne',
    nevada: 'nv',
    'new hampshire': 'nh',
    'new jersey': 'nj',
    'new mexico': 'nm',
    'new york': 'ny',
    'north carolina': 'nc',
    'north dakota': 'nd',
    ohio: 'oh',
    oklahoma: 'ok',
    oregon: 'or',
    pennsylvania: 'pa',
    'rhode island': 'ri',
    'south carolina': 'sc',
    'south dakota': 'sd',
    tennessee: 'tn',
    texas: 'tx',
    utah: 'ut',
    vermont: 'vt',
    virginia: 'va',
    washington: 'wa',
    'west virginia': 'wv',
    wisconsin: 'wi',
    wyoming: 'wy',
  };
  const normalizedState = normalizeString(state);
  return stateAbbreviations[normalizedState] || normalizedState;
};

interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  account?: {
    details?: {
      shipping_addresses?: Array<{
        postal_code?: string;
        city?: string;
        region?: string;
      }>;
    };
  };
}

export type HashedUserInfo = {
  hashedFirstName: string;
  hashedLastName: string;
  hashedEmail: string;
  hashedPhone: string;
  hashedZipCode: string;
  hashedCity: string;
  hashedState: string;
};

// this hashes the user info to use with CAPI
export const hashUserInfo = async ({
  firstName = '',
  lastName = '',
  email = '',
  phone = '',
  account,
}: UserInfo = {}): Promise<HashedUserInfo> => {
  const shippingAddress = account?.details?.shipping_addresses?.[0] || {};
  const { postal_code = '', city = '', region = '' } = shippingAddress;

  return {
    hashedFirstName: firstName
      ? await toSha256(normalizeString(firstName))
      : '',
    hashedLastName: lastName ? await toSha256(normalizeString(lastName)) : '',
    hashedEmail: email ? await toSha256(normalizeEmail(email)) : '',
    hashedPhone: phone ? await toSha256(normalizePhoneNumber(phone)) : '',
    hashedZipCode: postal_code
      ? await toSha256(normalizeZipCode(postal_code))
      : '',
    hashedCity: city ? await toSha256(normalizeString(city)) : '',
    hashedState: region ? await toSha256(normalizeState(region)) : '',
  };
};
